import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Pagination } from "@/components/molecules/Pagination";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SearchInput } from "@/components/molecules/SearchInput";
import { FilterBar } from "@/components/molecules/FilterBar";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { Tooltip } from "@/components/atoms/Tooltip";
import {
  DataTable,
  type Column,
  type DataTableCustomization,
} from "@/components/organisms/DataTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { FilterUrlSyncEffect, type FilterUrlSyncConfig } from "@/hooks/useFilterUrlSync";
import {
  SortUrlSyncEffect,
  initialPageWithUrlSort,
  sortDefaultsFromPage,
} from "@/hooks/useSortUrlSync";
import { usePagedResource } from "@/hooks/usePagedResource";
import { usePageScope, useSettingsStore, type ScopePageKey } from "@/store/settingsStore";
import { scopeParams } from "@/lib/scopeParams";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { useUiStore } from "@/store/uiStore";
import type { PageRequest, PageResponse, SortDirection } from "@/types/api";

interface GlobalFilterConfig {
  legalEntity?: boolean;
  branch?: boolean;
}

interface Props<T, F extends { search?: string }> {
  title: string;
  subtitle?: string;
  fetcher: (filter: F, page: PageRequest) => Promise<PageResponse<T>>;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** See `DataTable`. Set only where a row click opens a detail drawer. */
  markVisitedRow?: boolean;
  searchPlaceholder?: string;
  /** Hover hint explaining which fields the search box matches on. */
  searchTooltip?: string;
  emptyTitle?: string;
  initialFilter?: F;
  toolbar?: (state: {
    filter: F;
    setFilter: (next: Partial<F>) => void;
  }) => ReactNode;
  actions?: ReactNode;
  onBack?: () => void;
  backAriaLabel?: string;
  globalFilterConfig?: GlobalFilterConfig;
  /**
   * Renders the legal entity + branch scope picker in the filter bar, owning the named
   * page's selection. Omit on lists that carry no entity or branch axis.
   */
  scopePage?: ScopePageKey;
  showPageLoader?: boolean;
  initialPage?: PageRequest;
  /**
   * Bump to refetch the current page from outside. Used by pages that render
   * their own header (title="") and so can't use the built-in refresh button.
   */
  reloadToken?: number;
  /** Merges external filter changes into the list (e.g. header filters on host pages). */
  filterSync?: Partial<F>;
  /** Notifies the parent when the internal list loading state changes. */
  onLoadingChange?: (loading: boolean) => void;
  /** When set, keeps list filters in the address bar (replace, not push). */
  filterUrlSync?: FilterUrlSyncConfig<F>;
  tableCustomization?: DataTableCustomization;
}

/**
 * Generic list page wrapper used by the modules whose UX is a vanilla
 * filter+table+pagination. Contacts/Properties use bespoke pages because
 * they expose richer detail drawers and module-specific filters.
 */
export function SimpleListPage<T, F extends { search?: string } & object>({
  title,
  subtitle,
  fetcher,
  columns,
  rowKey,
  onRowClick,
  markVisitedRow,
  searchPlaceholder,
  searchTooltip,
  emptyTitle,
  initialFilter,
  toolbar,
  actions,
  onBack,
  backAriaLabel,
  globalFilterConfig = { legalEntity: true, branch: true },
  scopePage,
  showPageLoader = false,
  initialPage,
  reloadToken,
  filterSync,
  onLoadingChange,
  filterUrlSync,
  tableCustomization,
}: Props<T, F>) {
  const { legalEntityId, branchId } = useSettingsStore();
  const clearScope = useSettingsStore((s) => s.clearScope);
  const crmMappingEpoch = useCrmMappingSyncStore((s) => s.crmMappingEpoch);
  // Always subscribed, so the hook order never changes with the prop; an absent
  // scopePage simply reads a bucket nothing writes to.
  const pageScope = usePageScope(scopePage ?? "users");
  const scopeQuery = useMemo(
    () => (scopePage ? scopeParams(pageScope) : {}),
    [scopePage, pageScope],
  );

  const wrappedFetcher = useCallback(
    (f: F, p: PageRequest) => {
      const filters = { ...f };
      if (globalFilterConfig.legalEntity) {
        (filters as any).legalEntityId = legalEntityId || undefined;
      }
      if (globalFilterConfig.branch) {
        (filters as any).branchId = branchId || undefined;
      }
      // The picker's selection wins where it has one; the values above are the fallback.
      Object.assign(filters as object, scopeQuery);
      return fetcher(filters as F, p);
    },
    [fetcher, legalEntityId, branchId, crmMappingEpoch, globalFilterConfig.legalEntity, globalFilterConfig.branch, scopeQuery],
  );

  // Column sort lives in the URL (`?sort=…&sort_type=ASC|DESC`) on every list, so a
  // sorted view can be shared. The URL wins over `initialPage` for the first fetch;
  // `initialPage` still decides which sort counts as the default and stays unwritten.
  const [searchParams] = useSearchParams();
  const sortDefaults = useMemo(() => sortDefaultsFromPage(initialPage), [initialPage]);
  const resolvedInitialPage = useMemo(
    () => initialPageWithUrlSort(searchParams, initialPage),
    [searchParams, initialPage],
  );
  const list = usePagedResource<T, F>(wrappedFetcher, {
    initialFilter,
    initialPage: resolvedInitialPage,
  });
  const { startPageLoading, stopPageLoading } = useUiStore();

  const [search, setSearch] = useState(initialFilter?.search ?? list.filter.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  useEffect(() => {
    if (!showPageLoader) return;
    if (list.loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [list.loading, showPageLoader, startPageLoading, stopPageLoading]);

  useEffect(() => {
    onLoadingChange?.(list.loading);
  }, [list.loading, onLoadingChange]);

  useEffect(() => {
    list.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntityId, branchId, scopeQuery]);

  const filterSyncKey = JSON.stringify(filterSync ?? {});

  useEffect(() => {
    if (filterSync === undefined) return;
    list.setFilter(filterSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSyncKey]);

  const lastReloadToken = useRef(reloadToken);
  useEffect(() => {
    if (reloadToken === lastReloadToken.current) return;
    lastReloadToken.current = reloadToken;
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  useEffect(() => {
    if ((list.filter.search ?? "") === debouncedSearch) return;
    list.setFilter({ search: debouncedSearch || undefined } as Partial<F>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (!filterUrlSync) return;
    const next = list.filter.search ?? "";
    setSearch((prev) => {
      if (prev === next) return prev;
      // Only overwrite if input hasn't diverged from what was last debounced
      if (prev === debouncedSearchRef.current) {
        return next;
      }
      return prev;
    });
  }, [filterUrlSync, list.filter.search]);

  const sortObj = useMemo(
    () =>
      list.pageRequest.sort && list.pageRequest.direction
        ? { field: list.pageRequest.sort, direction: list.pageRequest.direction as SortDirection }
        : undefined,
    [list.pageRequest.sort, list.pageRequest.direction],
  );

  return (
    <div className="h-full flex flex-col">
      {filterUrlSync ? (
        <FilterUrlSyncEffect
          filter={list.filter}
          setFilter={list.setFilter}
          config={filterUrlSync}
          syncSearchInput={setSearch}
        />
      ) : null}
      <SortUrlSyncEffect
        sort={list.pageRequest.sort}
        direction={list.pageRequest.direction}
        setSort={list.setSort}
        defaults={sortDefaults}
      />
      {title && (
        <PageHeader
          title={title}
          subtitle={subtitle ?? `${list.data?.totalElements ?? 0} records.`}
          actions={
            <>
              <RefreshButton onClick={list.reload} loading={list.loading} />
              {actions}
            </>
          }
          onBack={onBack}
          backAriaLabel={backAriaLabel}
        />
      )}
      <div className="flex-1 mx-6 mb-6 glass rounded-lg overflow-hidden flex flex-col">
        <FilterBar
          onClear={() => {
            setSearch("");
            if (scopePage) clearScope(scopePage);
            if (filterUrlSync) list.setFilter(filterUrlSync.clearedFilter);
            else list.clearFilter();
          }}
        >
          {scopePage && (
            <ScopePicker page={scopePage} />
          )}
          <Tooltip side="top" hoverOnly content={searchTooltip ?? ""}>
            <div>
              <SearchInput value={search} onChange={setSearch} placeholder={searchPlaceholder ?? "Search..."} />
            </div>
          </Tooltip>
          {toolbar?.({ filter: list.filter, setFilter: list.setFilter })}
        </FilterBar>
        <div className="flex-1 overflow-auto scrollbar-metal">
          <DataTable
            rows={list.data?.content ?? []}
            columns={columns}
            loading={list.loading}
            rowKey={rowKey}
            onRowClick={onRowClick}
            markVisitedRow={markVisitedRow}
            sort={sortObj}
            onSortChange={list.setSort}
            emptyTitle={emptyTitle}
            customization={tableCustomization}
          />
        </div>
        <Pagination
          page={list.data?.page ?? 0}
          totalPages={list.data?.totalPages ?? 0}
          totalElements={list.data?.totalElements ?? 0}
          size={list.data?.size ?? 20}
          onPageChange={list.setPage}
        />
      </div>
    </div>
  );
}
